import { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getCut, deleteAudio, updateAudioLabel, addComment, addCommentWithAudio, deleteComment, addReply, updateComment, updateCut, deleteCut } from '../../api/cuts';
import { createApprovalRequest, respondToApprovalRequest, cancelApprovalRequest } from '../../api/approvals';
import { getProject } from '../../api/projects';
import { UploadModal } from '../../components/files/UploadModal';
import { useAuth } from '../../hooks/useAuth';
import { Cut, Comment, User } from '../../types';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { TimeInput, parseToSeconds } from '../../components/ui/TimeInput';
import { Loading } from '../../components/ui/Loading';
import { SideSheet, ConfirmationModal } from '../../components/ui/Modal';
import { Tabs, TabPanel } from '../../components/ui/Tabs';
import { Waveform, WaveformHandle } from '../../components/audio/Waveform';
import { CutFileExplorer } from '../../components/files/CutFileExplorer';
import { LyricsEditor } from '../../components/lyrics/LyricsEditor';
import { ActionSheet } from '../../components/ui/ActionMenu';
import { CutOverview } from '../../components/cuts/CutOverview';
import { EmojiPicker } from '../../components/chat/EmojiPicker';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';

// Icons for ActionMenu
const InfoIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const DeleteIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

// Tab type definition
type CutTab = 'overview' | 'audio' | 'files' | 'lyrics';

interface CommentItemProps {
  comment: Comment;
  cut: Cut;
  user: { id: string; role: string } | null;
  selectedAudioFileId: string | null;
  replyingToId: string | null;
  replyText: string;
  isAddingReply: boolean;
  replyAttachedAudio: File | null;
  onReplyAudioChange: (file: File | null) => void;
  editingCommentId: string | null;
  editingCommentContent: string;
  isEditingComment: boolean;
  highlightedCommentId: string | null;
  projectMembers: Pick<User, 'id' | 'name' | 'avatarUrl'>[];
  onStartReply: (commentId: string) => void;
  onCancelReply: () => void;
  onSubmitReply: (parentId: string) => void;
  onReplyTextChange: (text: string) => void;
  onStartEdit: (commentId: string, content: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: (commentId: string) => void;
  onEditContentChange: (content: string) => void;
  onDeleteComment: (commentId: string) => void;
  onRequestApproval: (commentId: string, targetUserId?: string) => void;
  onRespondApproval: (approvalId: string, status: 'APPROVED' | 'REJECTED') => void;
  onCancelApproval: (approvalId: string) => void;
  onCommentClick: (audioFileId: string, timestamp: number | null) => void;
  onSelectAudioFile: (audioFileId: string) => void;
  onCommentAdded: () => void;
  formatTime: (seconds: number) => string;
  threadColors?: Map<string, string>;
  threadColor?: string;
  depth: number;
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getFilenameWithoutExtension = (filename?: string | null) => {
  if (!filename) return '';

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex <= 0) return filename;

  return filename.slice(0, lastDotIndex);
};

// Module-level: generate a unique HSL color from an arbitrary string (e.g. comment ID)
const generateColorFromId = (id: string): string => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `hsl(${Math.abs(hash) % 360}, 70%, 50%)`;
};

const getColorFromId = (id: string) => {
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 
    'bg-purple-500', 'bg-pink-500', 'bg-indigo-500',
    'bg-red-500', 'bg-teal-500'
  ];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// ─── AttachedAudioPanel ────────────────────────────────────────────────────
// Full waveform player rendered inside a CommentItem when the comment has an
// attached audio file. Shares the same comment storage as the 1A standalone:
// all comments here are stored with managedFileId = attachedFile.id so they
// automatically appear in both views.

interface AttachedAudioPanelProps {
  cut: Cut;
  attachedFile: NonNullable<import('../../types').Comment['attachedFile']>;
  user: { id: string; role: string } | null;
  formatTime: (seconds: number) => string;
  onCommentAdded: () => void;
  onSelectStandalone: (fileId: string) => void;
}

function AttachedAudioPanel({ cut, attachedFile, user, formatTime, onCommentAdded, onSelectStandalone }: AttachedAudioPanelProps) {
  const waveformRef = useRef<WaveformHandle>(null);
  const [commentTimestamp, setCommentTimestamp] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Comments whose anchor is this attached file (same set shown in 1A standalone)
  const fileComments = useMemo(() =>
    (cut.comments ?? [])
      .filter(c => c.managedFileId === attachedFile.id)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [cut.comments, attachedFile.id]
  );

  const markers = useMemo(() =>
    fileComments
      .filter(c => c.timestamp !== null)
      .map(c => ({
        id: c.id,
        timestamp: c.timestamp!,
        color: generateColorFromId(c.id),
        user: c.user,
        content: c.content,
      })),
    [fileComments]
  );

  const handleAddComment = async () => {
    if (!commentText.trim() || !cut.id) return;
    setIsAdding(true);
    try {
      await addComment(cut.id, {
        content: commentText,
        timestamp: commentTimestamp,
        audioFileId: attachedFile.id,
      });
      setCommentText('');
      onCommentAdded();
    } catch (e) {
      console.error('Failed to add comment on attached audio:', e);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 overflow-hidden">
      {/* Panel header */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-primary/20">
        <div className="flex items-center gap-2 min-w-0">
          <svg className="w-3.5 h-3.5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
          </svg>
          <span className="text-xs font-medium text-primary truncate">
            {attachedFile.name || attachedFile.originalName}
          </span>
          {attachedFile.duration != null && (
            <span className="text-xs text-muted font-mono flex-shrink-0">
              {formatTime(attachedFile.duration)}
            </span>
          )}
          {fileComments.length > 0 && (
            <span className="text-xs text-muted flex-shrink-0">
              · {fileComments.length} comment{fileComments.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={() => onSelectStandalone(attachedFile.id)}
          className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
          title="Open standalone"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          View standalone
        </button>
      </div>

      {/* Waveform */}
      <div className="px-3 pt-3">
        <Waveform
          ref={waveformRef}
          audioUrl={`/${attachedFile.path}`}
          markers={markers}
          onTimeClick={(t) => setCommentTimestamp(t)}
          onMarkerClick={(t) => waveformRef.current?.seekTo(t)}
        />
      </div>

      {/* Timestamp + comment form */}
      <div className="px-3 pb-3 pt-2 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted">At:</span>
          <TimeInput
            value={formatTime(commentTimestamp)}
            onChange={(val) => {
              const secs = parseToSeconds(val);
              if (secs !== null) setCommentTimestamp(secs);
            }}
            onCommitSeconds={(secs) => waveformRef.current?.seekTo(secs)}
            className="text-center text-xs w-20"
          />
          <span className="text-xs text-muted">— click waveform to set</span>
        </div>
        <div className="flex gap-2">
          <EmojiPicker onEmojiSelect={(emoji) => setCommentText((prev) => prev + emoji)} buttonClassName="p-1 text-muted hover:text-text transition-colors flex-shrink-0" buttonLabel="Add emoji" />
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a timestamped comment..."
            className="flex-1 px-2.5 py-1.5 bg-background border border-border rounded text-text text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); }
            }}
          />
          <button
            onClick={handleAddComment}
            disabled={!commentText.trim() || isAdding || !user}
            className="px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAdding ? '…' : 'Add'}
          </button>
        </div>
      </div>

      {/* Existing comments on this file */}
      {fileComments.length > 0 && (
        <div className="border-t border-primary/20 divide-y divide-primary/10">
          {fileComments.map(c => (
            <div key={c.id} className="px-3 py-2 flex items-start gap-2">
              <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-bold text-white ${getColorFromId(c.userId ?? c.user?.id ?? '')}`}>
                {getInitials(c.user?.name ?? '?')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-xs font-medium text-text">{c.user?.name}</span>
                  {c.timestamp !== null && (
                    <button
                      onClick={() => waveformRef.current?.seekTo(c.timestamp!)}
                      className="text-[10px] font-mono text-primary hover:underline flex-shrink-0"
                      title="Seek to timestamp"
                    >
                      {formatTime(c.timestamp!)}
                    </button>
                  )}
                </div>
                <p className="text-xs text-text/80 leading-relaxed">{c.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CommentItem = memo(function CommentItem({
  comment,
  cut,
  user,
  selectedAudioFileId,
  replyingToId,
  replyText,
  isAddingReply,
  replyAttachedAudio,
  onReplyAudioChange,
  editingCommentId,
  editingCommentContent,
  isEditingComment,
  highlightedCommentId,
  projectMembers,
  onStartReply,
  onCancelReply,
  onSubmitReply,
  onReplyTextChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditContentChange,
  onDeleteComment,
  onRequestApproval,
  onRespondApproval,
  onCancelApproval,
  onCommentClick,
  onSelectAudioFile,
  onCommentAdded,
  formatTime,
  depth,
  threadColor,
}: CommentItemProps) {
  const audioIndex = cut.managedFiles?.findIndex(a => a.id === comment.managedFileId) ?? -1;
  const audioLabel = comment.managedFile?.name || comment.managedFile?.originalName || 'Unknown';
  const isReply = comment.parentId !== null;
  const isReplying = replyingToId === comment.id;
  const isEditing = editingCommentId === comment.id;
  const canModify = comment.userId === user?.id || user?.role === 'ADMIN';
  const hasTimestamp = comment.timestamp !== null;
  const isHighlighted = highlightedCommentId === comment.id;

  const handleTimestampClick = () => {
    if (hasTimestamp) {
      onCommentClick(comment.managedFileId, comment.timestamp);
    }
  };

  // Thread color kept for waveform marker consistency (passed down to replies)
  const color = threadColor;

  // Relative time formatting (e.g. "2 days ago", "just now")
  const getRelativeTime = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div id={`comment-${comment.id}`} className={depth > 0 ? 'ml-8 relative' : ''}>
      {/* Vertical connector line for replies */}
      {depth > 0 && (
        <div className="absolute left-[-20px] top-0 bottom-0 w-px bg-border" />
      )}

      {/* GitHub-style: avatar on left, bordered card on right */}
      <div className="flex gap-3 group">
        {/* Avatar column */}
        <div className="flex-shrink-0 pt-0.5">
          {comment.user?.avatarUrl ? (
            <img
              src={comment.user.avatarUrl}
              alt={comment.user.name}
              className="w-8 h-8 rounded-full object-cover ring-2 ring-border"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs text-white font-medium ring-2 ring-border ${getColorFromId(comment.user?.id ?? '0')}`}
            >
              {getInitials(comment.user?.name ?? comment.guestName ?? 'Guest')}
            </div>
          )}
        </div>

        {/* Comment card */}
        <div
          className={`flex-1 min-w-0 rounded-md border overflow-hidden transition-all ${
            isHighlighted
              ? 'border-primary ring-2 ring-primary shadow-lg animate-pulse'
              : 'border-border'
          }`}
        >
          {/* Header bar */}
          <div className={`flex items-center justify-between gap-2 px-3 py-2 text-xs border-b ${
            isHighlighted
              ? 'bg-primary/20 border-primary/30'
              : 'bg-accent/50 border-border'
          }`}>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="font-semibold text-text">{comment.user?.name ?? comment.guestName ?? 'Guest'}</span>
              {!isReply && (
                <>
                  <span className="text-muted">commented on</span>
                  <span className="text-primary font-medium truncate max-w-32" title={audioLabel}>
                    {audioLabel}
                  </span>
                  <span
                    className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium rounded ${
                      comment.managedFileId === selectedAudioFileId
                        ? 'bg-primary text-white'
                        : 'bg-muted/20 text-muted'
                    }`}
                  >
                    #{audioIndex + 1}
                  </span>
                </>
              )}
              <span className="text-muted" title={new Date(comment.createdAt).toLocaleString()}>
                {getRelativeTime(comment.createdAt)}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {hasTimestamp && (
                <button
                  onClick={handleTimestampClick}
                  className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[11px] rounded-full bg-primary/15 text-primary hover:bg-primary/25 transition-colors cursor-pointer"
                  title="Click to seek to this time"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {formatTime(comment.timestamp as number)}
                </button>
              )}
              {/* Delete button in header */}
              {canModify && !isEditing && (
                <button
                  onClick={() => onDeleteComment(comment.id)}
                  className="p-1 text-muted hover:text-error rounded transition-colors opacity-0 group-hover:opacity-100"
                  title="Delete"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Comment body */}
          <div className="px-3 py-3">
            {/* Comment content or edit form */}
            {isEditing ? (
              <div>
                <textarea
                  value={editingCommentContent}
                  onChange={(e) => onEditContentChange(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={3}
                  autoFocus
                />
                <div className="flex justify-end gap-2 mt-2">
                  <EmojiPicker onEmojiSelect={(emoji) => onEditContentChange(editingCommentContent + emoji)} buttonClassName="p-1 text-muted hover:text-text transition-colors flex-shrink-0 mr-auto" buttonLabel="Add emoji" />
                  <button
                    onClick={onCancelEdit}
                    className="px-3 py-1.5 text-sm text-muted hover:text-text border border-border rounded-md transition-colors"
                    disabled={isEditingComment}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => onSaveEdit(comment.id)}
                    disabled={!editingCommentContent.trim() || isEditingComment}
                    className="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isEditingComment ? 'Saving...' : 'Update comment'}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-text text-sm leading-relaxed whitespace-pre-wrap">{comment.content}</p>
            )}

            {/* Attached audio file — full waveform panel with timestamped comments */}
            {!isEditing && comment.attachedFile && (
              <AttachedAudioPanel
                cut={cut}
                attachedFile={comment.attachedFile}
                user={user}
                formatTime={formatTime}
                onCommentAdded={onCommentAdded}
                onSelectStandalone={onSelectAudioFile}
              />
            )}

            {/* Approval Request Status & Actions */}
            {!isEditing && comment.approvalRequest && (
              <div className="mt-3 p-2.5 rounded-md bg-background border border-border text-sm flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium text-text">Approval Request</span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                      comment.approvalRequest.status === 'PENDING' ? 'bg-yellow-500/20 text-yellow-500' :
                      comment.approvalRequest.status === 'APPROVED' ? 'bg-green-500/20 text-green-500' :
                      comment.approvalRequest.status === 'REJECTED' ? 'bg-red-500/20 text-red-500' :
                      'bg-gray-500/20 text-gray-500'
                    }`}>
                      {comment.approvalRequest.status}
                    </span>
                  </div>
                  {comment.approvalRequest.status === 'PENDING' && user?.id === comment.approvalRequest.requestedById && (
                    <button
                      onClick={() => onCancelApproval(comment.approvalRequest!.id)}
                      className="text-xs text-muted hover:text-error transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                
                {comment.approvalRequest.status !== 'PENDING' && comment.approvalRequest.resolvedBy && (
                  <div className="text-xs text-muted flex items-center gap-1">
                    Resolved by <span className="font-medium text-text">{comment.approvalRequest.resolvedBy.name}</span>
                  </div>
                )}

                {comment.approvalRequest.status === 'PENDING' && (
                  <div className="text-xs text-muted">
                    {comment.approvalRequest.targetUser ? (
                      <>Waiting for <span className="font-medium text-text">{comment.approvalRequest.targetUser.name}</span></>
                    ) : (
                      <>Waiting for feedback from any bandmate</>
                    )}
                  </div>
                )}

                {comment.approvalRequest.status === 'PENDING' && 
                  user && 
                  (user.id === comment.approvalRequest.targetUserId || !comment.approvalRequest.targetUserId || user.role === 'ADMIN') &&
                  user.id !== comment.approvalRequest.requestedById && (
                  <div className="flex gap-2 mt-1">
                    <button
                      onClick={() => onRespondApproval(comment.approvalRequest!.id, 'APPROVED')}
                      className="px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => onRespondApproval(comment.approvalRequest!.id, 'REJECTED')}
                      className="px-3 py-1 text-xs border border-red-500 text-red-500 rounded-md hover:bg-red-500/10 transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer actions bar */}
          {!isEditing && user && (
            <div className="flex items-center gap-1 px-3 py-2 border-t border-border">
              {!isReply && (
                <button
                  onClick={() => onStartReply(comment.id)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Reply
                </button>
              )}
              {canModify && (
                <button
                  onClick={() => onStartEdit(comment.id, comment.content)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Edit
                </button>
              )}
              {/* Request Approval Dropdown */}
              {!comment.approvalRequest && user.id === comment.userId && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-muted hover:text-yellow-500 hover:bg-yellow-500/10 rounded-md transition-colors"
                      title="Request approval for this comment"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Request Approval
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel className="text-xs text-muted font-normal">
                      Who should approve?
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onRequestApproval(comment.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-muted/30 flex items-center justify-center text-xs">
                          <svg className="w-3 h-3 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <span className="font-medium">Anyone</span>
                          <p className="text-xs text-muted">Any bandmate can respond</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted font-normal">
                      Specific bandmate
                    </DropdownMenuLabel>
                    {projectMembers
                      .filter(member => member.id !== user.id)
                      .map(member => (
                        <DropdownMenuItem
                          key={member.id}
                          onClick={() => onRequestApproval(comment.id, member.id)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            {member.avatarUrl ? (
                              <img
                                src={member.avatarUrl}
                                alt={member.name}
                                className="w-5 h-5 rounded-full object-cover"
                                referrerPolicy="no-referrer"
                              />
                            ) : (
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] text-white font-medium ${getColorFromId(member.id)}`}>
                                {getInitials(member.name)}
                              </div>
                            )}
                            <span>{member.name}</span>
                          </div>
                        </DropdownMenuItem>
                      ))}
                    {projectMembers.filter(m => m.id !== user.id).length === 0 && (
                      <DropdownMenuItem disabled className="text-xs text-muted">
                        No other bandmates
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Inline Reply Form - GitHub style */}
      {isReplying && (
        <div className="mt-3 ml-11 flex gap-3">
          {/* Reply avatar connector */}
          <div className="flex-shrink-0 pt-0.5">
            {user && (
              <div className="w-6 h-6 rounded-full bg-muted/30 flex items-center justify-center text-[10px] text-muted font-medium ring-1 ring-border">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </div>
            )}
          </div>
          <div className="flex-1 border border-border rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-accent/50 border-b border-border">
              <span className="text-xs text-muted">Reply to <span className="font-medium text-text">{comment.user?.name ?? comment.guestName ?? 'Guest'}</span></span>
            </div>
            <div className="p-3">
              <textarea
                value={replyText}
                onChange={(e) => onReplyTextChange(e.target.value)}
                placeholder="Write a reply..."
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={2}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 px-3 py-2 border-t border-border bg-accent/30">
              <EmojiPicker onEmojiSelect={(emoji) => onReplyTextChange(replyText + emoji)} buttonClassName="p-1 text-muted hover:text-text transition-colors flex-shrink-0 mr-auto" buttonLabel="Add emoji" />
              {/* Reply audio attach chip */}
              {replyAttachedAudio && (
                <div className="flex-1 flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10 border border-primary/20 text-xs text-primary min-w-0">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                  </svg>
                  <span className="truncate">{replyAttachedAudio.name}</span>
                  <button onClick={() => onReplyAudioChange(null)} className="flex-shrink-0 hover:text-error transition-colors ml-0.5">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              <label
                className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors cursor-pointer"
                title="Attach audio file to this reply"
              >
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => onReplyAudioChange(e.target.files?.[0] ?? null)}
                />
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 18.364a9 9 0 000-12.728M8.464 15.536a5 5 0 010-7.072" />
                </svg>
                Attach audio
              </label>
              <button
                onClick={onCancelReply}
                className="px-3 py-1.5 text-sm text-muted hover:text-text border border-border rounded-md transition-colors"
                disabled={isAddingReply}
              >
                Cancel
              </button>
              <button
                onClick={() => onSubmitReply(comment.id)}
                disabled={!replyText.trim() || isAddingReply}
                className="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isAddingReply ? 'Posting...' : 'Reply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nested Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-3 space-y-3">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              cut={cut}
              user={user}
              selectedAudioFileId={selectedAudioFileId}
              replyingToId={replyingToId}
              replyText={replyText}
              isAddingReply={isAddingReply}
              replyAttachedAudio={replyAttachedAudio}
              onReplyAudioChange={onReplyAudioChange}
              editingCommentId={editingCommentId}
              editingCommentContent={editingCommentContent}
              isEditingComment={isEditingComment}
              highlightedCommentId={highlightedCommentId}
              projectMembers={projectMembers}
              onStartReply={onStartReply}
              onCancelReply={onCancelReply}
              onSubmitReply={onSubmitReply}
              onReplyTextChange={onReplyTextChange}
              onStartEdit={onStartEdit}
              onCancelEdit={onCancelEdit}
              onSaveEdit={onSaveEdit}
              onEditContentChange={onEditContentChange}
              onDeleteComment={onDeleteComment}
              onRequestApproval={onRequestApproval}
              onRespondApproval={onRespondApproval}
              onCancelApproval={onCancelApproval}
              onCommentClick={onCommentClick}
              onSelectAudioFile={onSelectAudioFile}
              onCommentAdded={onCommentAdded}
              formatTime={formatTime}
              depth={depth + 1}
              threadColor={color}
            />
          ))}
        </div>
      )}
    </div>
  );
});

export function CutDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const commentAudioInputRef = useRef<HTMLInputElement>(null);
  const replyAudioInputRef = useRef<HTMLInputElement>(null);
  const waveformRefs = useRef<Map<string, WaveformHandle>>(new Map());
  
  const [cut, setCut] = useState<Cut | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [activeTab, setActiveTab] = useState<CutTab>(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'audio', 'files', 'lyrics'].includes(tabParam)) {
      return tabParam as CutTab;
    }
    return 'overview';
  });
  const [fileExplorerKey, setFileExplorerKey] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [commentTimestamp, setCommentTimestamp] = useState(0);
  const [selectedAudioFileId, setSelectedAudioFileId] = useState<string | null>(null);
  const [mobileAudioView, setMobileAudioView] = useState<'list' | 'detail'>('list');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [editingLabelValue, setEditingLabelValue] = useState('');
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isAddingReply, setIsAddingReply] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [isEditingComment, setIsEditingComment] = useState(false);
  
  // Audio attachments for comments
  const [commentAttachedAudio, setCommentAttachedAudio] = useState<File | null>(null);
  const [replyAttachedAudio, setReplyAttachedAudio] = useState<File | null>(null);
  
  // Project members for approval picker dropdown
  const [projectMembers, setProjectMembers] = useState<Pick<User, 'id' | 'name' | 'avatarUrl'>[]>([]);
  
  // Deep-link: highlight a specific comment from notification
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(
    searchParams.get('comment')
  );
  
  // React to search param changes when already on this page (e.g. notification click while viewing overview)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const commentParam = searchParams.get('comment');
    
    if (tabParam && ['overview', 'audio', 'files', 'lyrics'].includes(tabParam)) {
      setActiveTab(tabParam as CutTab);
    }
    if (commentParam) {
      setHighlightedCommentId(commentParam);
    }
  }, [searchParams]);
  
  // Comment marker state - for highlighting active markers
  const [activeMarkerId] = useState<string | null>(null);
  
  // Cut action modals
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editCutName, setEditCutName] = useState('');
  const [editCutBpm, setEditCutBpm] = useState<string>('');
  const [editCutTimeSignature, setEditCutTimeSignature] = useState<string>('');
  const [isUpdatingCut, setIsUpdatingCut] = useState(false);
  const [isDeletingCut, setIsDeletingCut] = useState(false);
  const [showDuplicateCutWarning, setShowDuplicateCutWarning] = useState(false);
  const [projectVibes, setProjectVibes] = useState<import('../../types').Vibe[]>([]);

  const handleSelectAudio = useCallback((audioId: string) => {
    setSelectedAudioFileId(audioId);
    setMobileAudioView('detail');
  }, []);

  const handleBackToList = useCallback(() => {
    setMobileAudioView('list');
  }, []);

  const fetchCut = async () => {
    if (!id) return;
    try {
      const data = await getCut(id);
      setCut(data);
      // Auto-select first playable audio file if none selected (desktop only)
      if (!selectedAudioFileId && data.managedFiles && data.managedFiles.length > 0) {
        const isDesktop = window.innerWidth >= 1024;
        if (isDesktop) {
          const firstPlayable = data.managedFiles.find(f => f.type !== 'AUDIO_FILE');
          if (firstPlayable) {
            setSelectedAudioFileId(firstPlayable.id);
          }
        }
      }
      // Fetch project members for the approval picker dropdown
      if (data.vibe?.project?.id) {
        try {
          const project = await getProject(data.vibe.project.id);
          setProjectMembers(
            project.members?.map(m => ({
              id: m.user.id,
              name: m.user.name,
              avatarUrl: (m.user as Pick<User, 'id' | 'name' | 'avatarUrl'>).avatarUrl,
            })) || []
          );
        } catch (err) {
          console.error('Failed to fetch project members:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch cut:', error);
      navigate('/projects');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCut();
  }, [id]);

  useEffect(() => {
    const handleProjectDataChanged = (event: Event) => {
      const custom = event as CustomEvent<{ projectId?: string; cutId?: string }>;
      if (!id) return;
      if (custom.detail?.cutId === id) {
        fetchCut();
        return;
      }
      if (custom.detail?.projectId && cut?.vibe?.project?.id === custom.detail.projectId) {
        fetchCut();
      }
    };

    window.addEventListener('bandmate:project-data-changed', handleProjectDataChanged);
    return () => window.removeEventListener('bandmate:project-data-changed', handleProjectDataChanged);
  }, [id, cut?.vibe?.project?.id]);

  // Deep-link: scroll to highlighted comment once cut data loads, then clean up URL
  useEffect(() => {
    if (!cut || !highlightedCommentId) return;

    // Find the comment (could be top-level or a reply)
    const findComment = (comments: Comment[]): Comment | undefined => {
      for (const c of comments) {
        if (c.id === highlightedCommentId) return c;
        if (c.replies) {
          const found = findComment(c.replies);
          if (found) return found;
        }
      }
      return undefined;
    };

    const comment = cut.comments ? findComment(cut.comments) : undefined;
    if (comment) {
      // Select the audio file this comment is on (switches to detail view on mobile)
      setSelectedAudioFileId(comment.managedFileId);
      setMobileAudioView('detail');

      // Scroll to the comment element after a brief delay for render
      setTimeout(() => {
        const el = document.getElementById(`comment-${highlightedCommentId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }

    // Clean up query params from URL without navigation
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('tab');
    newParams.delete('comment');
    setSearchParams(newParams, { replace: true });

    // Clear highlight after a few seconds
    const timer = setTimeout(() => setHighlightedCommentId(null), 4000);
    return () => clearTimeout(timer);
  }, [cut, highlightedCommentId]);

  // Build a single-entry hierarchy for the upload modal so project/vibe/cut are pre-populated
  const uploadHierarchy = useMemo(() => {
    if (!cut?.vibe?.project) return [];
    return [{
      id: cut.vibe.project.id,
      name: cut.vibe.project.name,
      slug: cut.vibe.project.slug,
      image: cut.vibe.project.image ?? null,
      vibes: [{
        id: cut.vibe.id,
        name: cut.vibe.name,
        slug: cut.vibe.slug,
        image: cut.vibe.image ?? null,
        cuts: [{ id: cut.id, name: cut.name, slug: cut.slug }],
      }],
    }];
  }, [cut]);

  const handleDeleteAudio = async (audioId: string) => {
    if (!confirm('Are you sure you want to delete this audio file? All comments on this audio will also be deleted.') || !id) return;
    try {
      await deleteAudio(id, audioId);
      // If we deleted the selected audio, reset selection
      if (selectedAudioFileId === audioId) {
        setSelectedAudioFileId(null);
      }
      fetchCut();
    } catch (error) {
      console.error('Failed to delete audio:', error);
    }
  };

  const handleStartEditLabel = (audioId: string, currentLabel: string | null) => {
    setEditingLabelId(audioId);
    setEditingLabelValue(currentLabel || '');
  };

  const handleSaveLabel = async (audioId: string) => {
    if (!id) return;
    try {
      await updateAudioLabel(id, audioId, editingLabelValue);
      setEditingLabelId(null);
      fetchCut();
    } catch (error) {
      console.error('Failed to update audio label:', error);
    }
  };

  const handleCancelEditLabel = () => {
    setEditingLabelId(null);
    setEditingLabelValue('');
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !id || !selectedAudioFileId) return;

    setIsAddingComment(true);
    try {
      if (commentAttachedAudio) {
        await addCommentWithAudio(id, {
          content: commentText,
          timestamp: commentTimestamp,
          audioFileId: selectedAudioFileId,
          file: commentAttachedAudio,
        });
        setCommentAttachedAudio(null);
        if (commentAudioInputRef.current) commentAudioInputRef.current.value = '';
      } else {
        await addComment(id, {
          content: commentText,
          timestamp: commentTimestamp,
          audioFileId: selectedAudioFileId,
        });
      }
      setCommentText('');
      fetchCut();
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!id) return;
    try {
      await deleteComment(id, commentId);
      fetchCut();
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleStartReply = useCallback((commentId: string) => {
    setReplyingToId(commentId);
    setReplyText('');
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyingToId(null);
    setReplyText('');
  }, []);

  const handleSubmitReply = async (parentId: string) => {
    if (!replyText.trim() || !id) return;

    setIsAddingReply(true);
    try {
      if (replyAttachedAudio) {
        await addCommentWithAudio(id, {
          content: replyText,
          parentId,
          file: replyAttachedAudio,
        });
        setReplyAttachedAudio(null);
        if (replyAudioInputRef.current) replyAudioInputRef.current.value = '';
      } else {
        await addReply(id, parentId, replyText);
      }
      setReplyingToId(null);
      setReplyText('');
      fetchCut();
    } catch (error) {
      console.error('Failed to add reply:', error);
    } finally {
      setIsAddingReply(false);
    }
  };

  const handleStartEditComment = useCallback((commentId: string, content: string) => {
    setEditingCommentId(commentId);
    setEditingCommentContent(content);
  }, []);

  const handleCancelEditComment = useCallback(() => {
    setEditingCommentId(null);
    setEditingCommentContent('');
  }, []);

  const handleSaveEditComment = async (commentId: string) => {
    if (!editingCommentContent.trim() || !id) return;

    setIsEditingComment(true);
    try {
      await updateComment(id, commentId, editingCommentContent);
      setEditingCommentId(null);
      setEditingCommentContent('');
      fetchCut();
    } catch (error) {
      console.error('Failed to update comment:', error);
    } finally {
      setIsEditingComment(false);
    }
  };

  const handleRequestApproval = async (commentId: string, targetUserId?: string) => {
    try {
      await createApprovalRequest({ commentId, targetUserId });
      fetchCut();
    } catch (error) {
      console.error('Failed to request approval:', error);
      alert('Failed to request approval. Please try again.');
    }
  };

  const handleRespondApproval = async (approvalId: string, status: 'APPROVED' | 'REJECTED') => {
    try {
      await respondToApprovalRequest(approvalId, { status });
      fetchCut();
    } catch (error) {
      console.error('Failed to respond to approval request:', error);
      alert('Failed to respond to approval request. Please try again.');
    }
  };

  const handleCancelApproval = async (approvalId: string) => {
    try {
      await cancelApprovalRequest(approvalId);
      fetchCut();
    } catch (error) {
      console.error('Failed to cancel approval request:', error);
      alert('Failed to cancel approval request. Please try again.');
    }
  };

  const handleCommentClick = useCallback((audioFileId: string, timestamp: number | null) => {
    if (timestamp === null) return;
    
    // Select the audio file and switch to detail view
    setSelectedAudioFileId(audioFileId);
    setMobileAudioView('detail');
    
    // Seek the waveform to the timestamp
    const waveform = waveformRefs.current.get(audioFileId);
    if (waveform) {
      waveform.seekTo(timestamp);
    }
  }, []);

  // Handler for lyrics seeking
  const handleLyricsSeek = (audioFileId: string, timestamp: number) => {
    setSelectedAudioFileId(audioFileId);
    setMobileAudioView('detail');
    const waveform = waveformRefs.current.get(audioFileId);
    if (waveform) {
      waveform.seekTo(timestamp);
    }
  };

  // Get current playback time from selected audio
  const getCurrentPlaybackTime = (): number => {
    if (!selectedAudioFileId) return 0;
    const waveform = waveformRefs.current.get(selectedAudioFileId);
    if (waveform) {
      return waveform.getCurrentTime?.() ?? 0;
    }
    return 0;
  };

  const setWaveformRef = (audioId: string) => (ref: WaveformHandle | null) => {
    if (ref) {
      waveformRefs.current.set(audioId, ref);
    } else {
      waveformRefs.current.delete(audioId);
    }
  };

  const handleTimeClick = (audioFileId: string) => (time: number) => {
    setCommentTimestamp(time);
    setSelectedAudioFileId(audioFileId);
  };

  const handleMarkerClick = (audioFileId: string) => (timestamp: number) => {
    // Select the audio file (expands accordion) and seek to the comment
    setSelectedAudioFileId(audioFileId);
    const comments = getCommentsForAudio(audioFileId).filter(c => c.timestamp !== null);
    const firstComment = comments.find(c => Math.round(c.timestamp!) === timestamp);
    if (firstComment) {
      handleCommentClick(audioFileId, timestamp);
      // Scroll to the comment after accordion expands
      setTimeout(() => {
        const el = document.getElementById(`comment-${firstComment.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  };

  const formatTime = useCallback((seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Generate unique HSL color from string (e.g., comment ID)
  const generateColorFromId = (id: string): string => {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      const char = id.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    // Use hash to generate hue (0-360), keep saturation and lightness consistent
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  // Generate colors for comment threads
  const generateThreadColors = (comments: Comment[]): Map<string, string> => {
    const threadColors = new Map<string, string>();
    const topLevelComments = comments.filter(c => !c.parentId);
    
    topLevelComments.forEach((comment) => {
      const color = generateColorFromId(comment.id);
      // Assign color to this comment and all its replies
      threadColors.set(comment.id, color);
      assignReplyColors(comment, color, threadColors);
    });
    
    return threadColors;
  };
  
  const assignReplyColors = (comment: Comment, color: string, colorMap: Map<string, string>) => {
    if (comment.replies) {
      comment.replies.forEach(reply => {
        colorMap.set(reply.id, color);
        assignReplyColors(reply, color, colorMap);
      });
    }
  };

  // Get comment markers with thread colors for waveform
  const getCommentMarkers = useCallback((audioFileId: string): import('../../types').CommentMarker[] => {
    const allComments = cut?.comments?.filter(c => c.managedFileId === audioFileId) || [];
    const commentsWithTimestamps = allComments.filter(c => c.timestamp !== null);
    
    if (commentsWithTimestamps.length === 0) return [];
    
    // Generate thread colors
    const threadColors = generateThreadColors(allComments);
    
    // Create markers for each comment with timestamp
    return commentsWithTimestamps.map(comment => ({
      id: comment.id,
      timestamp: comment.timestamp!,
      color: threadColors.get(comment.id) || `hsl(${getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()})`,
      user: comment.user,
      content: comment.content,
    }));
  }, [cut?.comments]);

  const getCommentsForAudio = (audioFileId: string) => {
    return cut?.comments
      ?.filter(c => c.managedFileId === audioFileId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) || [];
  };

  // Map: attachedFileId → Comment (the comment that spawned that file)
  // Map: anchorFileId → attachedFileIds[] (children)
  // Map: attachedFileId → anchorFileId (parent lookup)
  const { attachedFileCommentMap, anchorToChildIds, childToAnchorId } = useMemo(() => {
    const attachedFileCommentMap = new Map<string, Comment>();
    const anchorToChildIds = new Map<string, string[]>();
    const childToAnchorId = new Map<string, string>();

    const scanComments = (comments: Comment[]) => {
      for (const c of comments) {
        if (c.attachedFileId && c.managedFileId) {
          attachedFileCommentMap.set(c.attachedFileId, c);
          childToAnchorId.set(c.attachedFileId, c.managedFileId);
          const existing = anchorToChildIds.get(c.managedFileId) ?? [];
          existing.push(c.attachedFileId);
          anchorToChildIds.set(c.managedFileId, existing);
        }
        if (c.replies?.length) scanComments(c.replies);
      }
    };
    if (cut?.comments) scanComments(cut.comments);
    return { attachedFileCommentMap, anchorToChildIds, childToAnchorId };
  }, [cut?.comments]);

  // Ordered display list: each anchor followed immediately by its children
  // AUDIO_FILE entries are excluded — they only appear in the Files tab
  const audioDisplayList = useMemo(() => {
    const files = (cut?.managedFiles ?? []).filter(f => f.type !== 'AUDIO_FILE');
    const childIds = new Set(childToAnchorId.keys());
    const result: { file: (typeof files)[number]; isChild: boolean }[] = [];

    for (const file of files) {
      if (childIds.has(file.id)) continue; // children are inserted after their anchor
      result.push({ file, isChild: false });
      const children = (anchorToChildIds.get(file.id) ?? [])
        .map(cid => files.find(f => f.id === cid))
        .filter(Boolean) as typeof files;
      for (const child of children) {
        result.push({ file: child, isChild: true });
      }
    }
    return result;
  }, [cut?.managedFiles, childToAnchorId, anchorToChildIds]);

  // Cut action handlers
  const handleOpenEditModal = async () => {
    if (cut) {
      setEditCutName(cut.name);
      setEditCutBpm(cut.bpm?.toString() || '');
      setEditCutTimeSignature(cut.timeSignature || '');
      setShowEditModal(true);
      // Fetch project vibes for duplicate name detection
      if (cut.vibe?.project?.id) {
        try {
          const project = await getProject(cut.vibe.project.id);
          setProjectVibes(project.vibes || []);
        } catch (err) {
          console.error('Failed to fetch project vibes:', err);
        }
      }
    }
  };

  // Check if a cut name already exists anywhere in the project (excluding current cut)
  const findDuplicateCutInProject = (name: string): { cutName: string; vibeName: string } | null => {
    const trimmed = name.trim().toLowerCase();
    for (const v of projectVibes) {
      const match = v.cuts?.find(c => c.name.toLowerCase() === trimmed && c.id !== id);
      if (match) return { cutName: match.name, vibeName: v.name };
    }
    return null;
  };

  const handleSaveCutEdit = async () => {
    if (!id || !editCutName.trim()) return;

    // Check for duplicate name if the name changed
    if (cut && editCutName.trim().toLowerCase() !== cut.name.toLowerCase()) {
      const duplicate = findDuplicateCutInProject(editCutName);
      if (duplicate && !showDuplicateCutWarning) {
        setShowDuplicateCutWarning(true);
        return;
      }
    }

    await submitCutEdit();
  };

  const submitCutEdit = async () => {
    if (!id || !editCutName.trim()) return;
    
    setIsUpdatingCut(true);
    try {
      await updateCut(id, {
        name: editCutName,
        bpm: editCutBpm ? parseInt(editCutBpm, 10) : null,
        timeSignature: editCutTimeSignature || null,
      });
      setShowEditModal(false);
      setShowDuplicateCutWarning(false);
      fetchCut();
    } catch (error) {
      console.error('Failed to update cut:', error);
    } finally {
      setIsUpdatingCut(false);
    }
  };

  const handleDeleteCut = async () => {
    if (!id || !cut) return;
    
    setIsDeletingCut(true);
    try {
      await deleteCut(id);
      // Navigate back to the project page after deletion
      navigate(`/projects/${cut.vibe?.project?.id}`);
    } catch (error) {
      console.error('Failed to delete cut:', error);
      setIsDeletingCut(false);
    }
  };

  if (isLoading) {
    return <Loading className="py-12" />;
  }

  if (!cut) {
    return (
      <Card className="text-center py-12">
        <p className="text-muted">Cut not found</p>
        <Link to="/projects" className="text-primary hover:underline mt-2 inline-block">
          Back to projects
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm text-muted">
        <Link to="/projects" className="hover:text-text">Projects</Link>
        <span className="mx-2">/</span>
        <Link to={`/projects/${cut.vibe?.project?.id}`} className="hover:text-text">
          {cut.vibe?.project?.name}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-muted">{cut.vibe?.name}</span>
        <span className="mx-2">/</span>
        <span className="text-text">{cut.name}</span>
        <ActionSheet
          className="ml-1 p-1"
          title={cut.name}
          items={[
            {
              label: 'View Info',
              description: 'See cut details and metadata',
              icon: <InfoIcon />,
              onClick: () => setShowInfoModal(true),
            },
            {
              label: 'Edit Details',
              description: 'Update name, BPM, and time signature',
              icon: <EditIcon />,
              onClick: handleOpenEditModal,
              visible: isAdmin,
            },
            {
              label: 'Delete Cut',
              description: 'Permanently remove this cut',
              icon: <DeleteIcon />,
              onClick: () => setShowDeleteModal(true),
              variant: 'danger',
              visible: isAdmin,
            },
          ]}
        />
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-text">{cut.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            {cut.bpm !== null && cut.bpm !== undefined && (
              <span className="inline-flex items-center px-2 py-1 text-sm font-medium rounded-full bg-primary/20 text-primary">
                {cut.bpm} BPM
              </span>
            )}
            {cut.timeSignature && (
              <span className="inline-flex items-center px-2 py-1 text-sm font-medium rounded-full bg-primary/10 text-primary">
                {cut.timeSignature}
              </span>
            )}
          </div>
          <p className="text-muted mt-1">
            {cut.managedFiles?.length || 0} audio files • {cut.comments?.length || 0} comments
          </p>
        </div>
        <div>
          {(activeTab === 'audio' || activeTab === 'files') && (
            <Button onClick={() => setShowUploadModal(true)}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload File
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'audio', label: 'Audio', badge: cut.managedFiles?.length || 0 },
          { id: 'files', label: 'Files' },
          { id: 'lyrics', label: 'Lyrics' },
        ]}
        activeTab={activeTab}
        onChange={(tabId) => setActiveTab(tabId as CutTab)}
      />

      {/* Overview Tab Content */}
      <TabPanel id="overview" activeTab={activeTab}>
        <CutOverview cutId={cut.id} />
      </TabPanel>

      {/* Audio Tab Content - Master-Detail Layout */}
      <TabPanel id="audio" activeTab={activeTab}>
        {cut.managedFiles?.length === 0 ? (
          <Card className="text-center py-12">
            <svg className="w-12 h-12 text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            <p className="text-muted">No audio files yet</p>
            <p className="text-sm text-muted mt-1">Upload an audio file to get started</p>
          </Card>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 min-h-[400px] lg:h-[calc(100vh-280px)]">
            {/* Master: Audio List */}
            <div className={`lg:w-80 lg:flex-shrink-0 flex flex-col ${mobileAudioView === 'detail' ? 'hidden lg:flex' : ''}`}>
              <div className="flex-1 overflow-y-auto pr-2 max-h-[300px] lg:max-h-none">
                <div className="space-y-1">
                  {(() => {
                    // Track anchor numbering separately from display index
                    let anchorNum = 0;
                    const anchorNumMap = new Map<string, number>();
                    // Pre-assign anchor numbers
                    for (const { file, isChild } of audioDisplayList) {
                      if (!isChild) anchorNumMap.set(file.id, ++anchorNum);
                    }
                    // Count how many children each anchor has rendered so far
                    const childCountMap = new Map<string, number>();

                    return audioDisplayList.map(({ file: audio, isChild }) => {
                      const audioComments = getCommentsForAudio(audio.id);
                      const isSelected = selectedAudioFileId === audio.id;
                      const anchorId = childToAnchorId.get(audio.id);
                      // Secondary highlight: anchor is "related" when one of its children is selected
                      const isRelatedAnchor = !isChild && anchorToChildIds.get(audio.id)?.includes(selectedAudioFileId ?? '') === true;
                      const fromComment = attachedFileCommentMap.get(audio.id);

                      // Build label: "1", "2" for anchors; "1A", "1B" for children
                      let label: string;
                      if (isChild && anchorId !== undefined) {
                        const parentNum = anchorNumMap.get(anchorId) ?? 0;
                        const count = childCountMap.get(anchorId) ?? 0;
                        childCountMap.set(anchorId, count + 1);
                        label = `${parentNum}${'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[count]}`;
                      } else {
                        label = String(anchorNumMap.get(audio.id) ?? '?');
                      }

                      const card = (
                        <div
                          key={audio.id}
                          onClick={() => handleSelectAudio(audio.id)}
                          className={`p-3 rounded-lg cursor-pointer transition-all border-2 ${
                            isSelected
                              ? 'bg-primary/10 border-primary'
                              : isRelatedAnchor
                              ? 'bg-primary/5 border-primary/40'
                              : 'bg-surface hover:bg-surface-light border-transparent'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 text-xs font-medium rounded ${
                                  isSelected
                                    ? 'bg-primary text-white'
                                    : isChild
                                    ? 'bg-primary/15 text-primary'
                                    : 'bg-primary/20 text-primary'
                                }`}>
                                  {label}
                                </span>
                                <span className={`text-sm font-medium truncate ${isChild ? 'text-text/80' : 'text-text'}`}>
                                  {audio.name || getFilenameWithoutExtension(audio.originalName) || 'No label'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1.5 text-xs text-muted flex-wrap">
                                {!isChild && (
                                  <>
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                    </svg>
                                    <span>{audioComments.length}</span>
                                  </>
                                )}
                                {audio.uploadedBy && (
                                  <>
                                    {!isChild && <span>•</span>}
                                    <span className="truncate">{audio.uploadedBy.name}</span>
                                  </>
                                )}
                                {isChild && fromComment && (
                                  <span className="text-muted/70 italic">
                                    from @{fromComment.user?.name ?? 'comment'}
                                  </span>
                                )}
                              </div>
                            </div>
                            {audio.duration && (
                              <span className="text-xs text-muted font-mono flex-shrink-0">
                                {formatTime(audio.duration)}
                              </span>
                            )}
                          </div>
                        </div>
                      );

                      // Wrap child in an indented connector
                      if (isChild) {
                        return (
                          <div key={audio.id} className="flex items-stretch gap-0">
                            {/* Connector line */}
                            <div className="flex flex-col items-center w-5 flex-shrink-0">
                              <div className="w-px flex-1 bg-border ml-2.5" />
                              <div className="w-2.5 h-px bg-border mb-3" />
                            </div>
                            <div className="flex-1 min-w-0 pb-1">
                              {card}
                            </div>
                          </div>
                        );
                      }

                      return card;
                    });
                  })()}
                </div>
              </div>
            </div>

            {/* Detail: Selected Audio */}
            <div className={`flex-1 min-w-0 ${!selectedAudioFileId || mobileAudioView === 'list' ? 'hidden lg:block' : ''}`}>
              {selectedAudioFileId ? (
                (() => {
                  const audio = cut.managedFiles?.find(f => f.id === selectedAudioFileId);
                  if (!audio) return null;
                  const audioComments = getCommentsForAudio(audio.id);
                  const commentMarkers = getCommentMarkers(audio.id);
                  const audioThreadColors = generateThreadColors(audioComments);

                  return (
                    <div className="h-full flex flex-col space-y-4">
                      {/* Mobile back button - only visible below lg breakpoint */}
                      <button
                        onClick={handleBackToList}
                        className="lg:hidden flex items-center gap-1.5 text-sm text-muted hover:text-text transition-colors self-start"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to list
                      </button>

                      {/* Audio Header */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {editingLabelId === audio.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editingLabelValue}
                                onChange={(e) => setEditingLabelValue(e.target.value)}
                                placeholder="e.g., Mix v2, Drums Only, Final Master"
                                className="flex-1 px-3 py-1.5 bg-background border border-border rounded text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveLabel(audio.id);
                                  if (e.key === 'Escape') handleCancelEditLabel();
                                }}
                              />
                              <button
                                onClick={() => handleSaveLabel(audio.id)}
                                className="p-1.5 text-primary hover:bg-primary/10 rounded transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                onClick={handleCancelEditLabel}
                                className="p-1.5 text-muted hover:text-error hover:bg-error/10 rounded transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-text">
                                {audio.name || getFilenameWithoutExtension(audio.originalName) || 'No label'}
                              </h3>
                              <button
                                onClick={() => handleStartEditLabel(audio.id, audio.name)}
                                className="p-1 text-muted hover:text-primary transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted">
                            <span>{audio.originalName}</span>
                            {audio.uploadedBy && (
                              <>
                                <span>•</span>
                                <span>Uploaded by {audio.uploadedBy.name}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteAudio(audio.id)}
                          className="p-2 text-muted hover:text-error hover:bg-error/10 rounded transition-colors"
                          title="Delete audio file"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>

                      {/* Waveform */}
                      <div onClick={(e) => e.stopPropagation()}>
                        <Waveform
                          ref={setWaveformRef(audio.id)}
                          audioUrl={`/${audio.path}`}
                          onTimeClick={handleTimeClick(audio.id)}
                          markers={commentMarkers}
                          onMarkerClick={handleMarkerClick(audio.id)}
                          activeMarkerId={activeMarkerId}
                        />
                      </div>

                      {/* Comments Section */}
                      <div className="flex-1 overflow-hidden flex flex-col">
                        <h3 className="font-semibold text-text mb-3">
                          Comments ({audioComments.length})
                        </h3>
                        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                          {/* Add Comment Form */}
                          <Card className="!py-3">
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-muted mb-1">
                                  Timestamp
                                </label>
                                <TimeInput
                                  value={formatTime(commentTimestamp)}
                                  onChange={(val) => {
                                    const secs = parseToSeconds(val);
                                    if (secs !== null) {
                                      setCommentTimestamp(secs);
                                    }
                                  }}
                                  onCommitSeconds={(secs) => {
                                    handleCommentClick(audio.id, secs);
                                  }}
                                  className="text-center text-sm"
                                />
                                <span className="text-xs text-muted block mt-1">Click on waveform to set</span>
                              </div>
                              <Input
                                label="Comment"
                                placeholder="Add your feedback..."
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                              />
                              {/* Attach audio chip */}
                              {commentAttachedAudio && (
                                <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-primary/10 border border-primary/30 text-xs text-primary">
                                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                                  </svg>
                                  <span className="flex-1 truncate">{commentAttachedAudio.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => { setCommentAttachedAudio(null); if (commentAudioInputRef.current) commentAudioInputRef.current.value = ''; }}
                                    className="text-primary/70 hover:text-primary flex-shrink-0"
                                    aria-label="Remove attachment"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <EmojiPicker onEmojiSelect={(emoji) => setCommentText((prev) => prev + emoji)} buttonClassName="p-1 text-muted hover:text-text transition-colors flex-shrink-0" buttonLabel="Add emoji" />
                                <label
                                  htmlFor="comment-audio-attach"
                                  title="Attach audio file"
                                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border text-sm text-muted hover:text-text hover:border-text/30 cursor-pointer transition-colors"
                                >
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 18.364V5.636M8.464 8.464a5 5 0 000 7.072" />
                                  </svg>
                                  Attach audio
                                  <input
                                    id="comment-audio-attach"
                                    ref={commentAudioInputRef}
                                    type="file"
                                    accept="audio/*"
                                    className="sr-only"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) setCommentAttachedAudio(f);
                                    }}
                                  />
                                </label>
                                <Button
                                  onClick={handleAddComment}
                                  isLoading={isAddingComment}
                                  disabled={!commentText.trim()}
                                  className="flex-1"
                                  size="sm"
                                >
                                  Add Comment
                                </Button>
                              </div>
                            </div>
                          </Card>

                          {/* Comments List */}
                          {audioComments.map((comment) => (
                            <CommentItem
                              key={comment.id}
                              comment={comment}
                              cut={cut}
                              user={user}
                              selectedAudioFileId={selectedAudioFileId}
                              replyingToId={replyingToId}
                              replyText={replyText}
                              isAddingReply={isAddingReply}
                              replyAttachedAudio={replyAttachedAudio}
                              onReplyAudioChange={setReplyAttachedAudio}
                              editingCommentId={editingCommentId}
                              editingCommentContent={editingCommentContent}
                              isEditingComment={isEditingComment}
                              highlightedCommentId={highlightedCommentId}
                              projectMembers={projectMembers}
                              onStartReply={handleStartReply}
                              onCancelReply={handleCancelReply}
                              onSubmitReply={handleSubmitReply}
                              onReplyTextChange={setReplyText}
                              onStartEdit={handleStartEditComment}
                              onCancelEdit={handleCancelEditComment}
                              onSaveEdit={handleSaveEditComment}
                              onEditContentChange={setEditingCommentContent}
                              onDeleteComment={handleDeleteComment}
                              onRequestApproval={handleRequestApproval}
                              onRespondApproval={handleRespondApproval}
                              onCancelApproval={handleCancelApproval}
                              onCommentClick={handleCommentClick}
                              onSelectAudioFile={handleSelectAudio}
                              onCommentAdded={fetchCut}
                              formatTime={formatTime}
                              depth={0}
                              threadColor={audioThreadColors.get(comment.id)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="h-full flex items-center justify-center text-muted">
                  <div className="text-center">
                    <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                    <p>Select an audio file to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </TabPanel>

      {/* Files Tab Content */}
      <TabPanel id="files" activeTab={activeTab}>
        <CutFileExplorer 
          key={fileExplorerKey}
          cutId={id!}
          vibeName={cut.vibe?.name}
          vibeImage={cut.vibe?.image}
          projectName={cut.vibe?.project?.name}
          projectImage={cut.vibe?.project?.image}
          onUploadRequest={() => setShowUploadModal(true)}
          onFileChange={fetchCut}
        />
      </TabPanel>

      {/* Lyrics Tab Content */}
      <TabPanel id="lyrics" activeTab={activeTab}>
        <LyricsEditor
          cutId={id!}
          audioFiles={cut.managedFiles || []}
          onSeek={handleLyricsSeek}
          getCurrentTime={getCurrentPlaybackTime}
        />
      </TabPanel>

      {/* Cut Info Modal */}
      <SideSheet
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        title="Cut Information"
        description="Details about this cut"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted mb-1">Name</label>
            <p className="text-text">{cut.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted mb-1">Project</label>
            <p className="text-text">{cut.vibe?.project?.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted mb-1">Vibe</label>
            <p className="text-text">{cut.vibe?.name}</p>
          </div>
          <div className="flex gap-8">
            <div>
              <label className="block text-sm font-medium text-muted mb-1">BPM</label>
              <p className="text-text">{cut.bpm || 'Not set'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted mb-1">Time Signature</label>
              <p className="text-text">{cut.timeSignature || 'Not set'}</p>
            </div>
          </div>
          <div className="flex gap-8">
            <div>
              <label className="block text-sm font-medium text-muted mb-1">Audio Files</label>
              <p className="text-text">{cut.managedFiles?.length || 0}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted mb-1">Comments</label>
              <p className="text-text">{cut.comments?.length || 0}</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted mb-1">Created</label>
            <p className="text-text">{new Date(cut.createdAt).toLocaleDateString()}</p>
          </div>
        </div>
      </SideSheet>

      {/* Cut Edit Modal */}
      <SideSheet
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Cut"
        description="Update cut details"
        footer={
          <Button onClick={handleSaveCutEdit} isLoading={isUpdatingCut}>
            Save Changes
          </Button>
        }
      >
        <div className="space-y-4">
          <Input
            label="Name"
            placeholder="Enter cut name..."
            value={editCutName}
            onChange={(e) => setEditCutName(e.target.value)}
          />
          <Input
            label="BPM (Beats Per Minute)"
            type="number"
            placeholder="e.g., 120"
            value={editCutBpm}
            onChange={(e) => setEditCutBpm(e.target.value)}
            min={1}
            max={999}
          />
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Time Signature
            </label>
            <select
              value={editCutTimeSignature}
              onChange={(e) => setEditCutTimeSignature(e.target.value)}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select time signature</option>
              <option value="4/4">4/4 (Common Time)</option>
              <option value="3/4">3/4 (Waltz)</option>
              <option value="6/8">6/8</option>
              <option value="2/4">2/4</option>
              <option value="5/4">5/4</option>
              <option value="7/8">7/8</option>
              <option value="12/8">12/8</option>
            </select>
          </div>
        </div>
      </SideSheet>

      {/* Cut Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Cut"
        description="This action cannot be undone"
      >
        <div className="space-y-4">
          <p className="text-muted">
            Are you sure you want to delete{' '}
            <span className="text-text font-medium">{cut.name}</span>?
            This will also delete all audio files, comments, and lyrics associated with this cut.
          </p>
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)} disabled={isDeletingCut}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteCut} isLoading={isDeletingCut}>
              Delete Cut
            </Button>
          </div>
        </div>
      </ConfirmationModal>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          fetchCut();
          setFileExplorerKey(prev => prev + 1);
        }}
        hierarchy={uploadHierarchy}
        defaultVibeId={cut?.vibe?.id}
        defaultCutId={cut?.id}
        lockHierarchy
      />

      {/* Duplicate Cut Name Warning */}
      <ConfirmationModal
        isOpen={showDuplicateCutWarning}
        onClose={() => setShowDuplicateCutWarning(false)}
        title="Duplicate Cut Name"
        description="A cut with this name already exists"
      >
        <div className="space-y-4">
          <p className="text-muted">
            A cut named{' '}
            <span className="text-text font-medium">"{editCutName.trim()}"</span>{' '}
            already exists in the{' '}
            <span className="text-text font-medium">
              "{findDuplicateCutInProject(editCutName)?.vibeName}"
            </span>{' '}
            vibe. Are you sure you want to continue?
          </p>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button variant="ghost" onClick={() => setShowDuplicateCutWarning(false)} disabled={isUpdatingCut}>
              Cancel
            </Button>
            <Button onClick={submitCutEdit} isLoading={isUpdatingCut}>
              Save Anyway
            </Button>
          </div>
        </div>
      </ConfirmationModal>
    </div>
  );
}
